/**
 * Derivazione client-side dello stato SLA per i finding KEV (Dashboard KEV / G1).
 *
 * Le finestre CRA Art.14 (24h prima notifica / 72h conferma / 14gg report finale) sono calcolate
 * dall'istante di detection (`detectedAt` = capturedAt dello snapshot, vedi aggregation-service).
 * Pura: nessun timer persistito necessario per la vista.
 */

export type SlaStatus = 'ok' | 'warning' | 'breached';

export interface SlaThreshold {
  /** Etichetta della soglia. */
  label: '24h' | '72h' | '14gg';
  /** Millisecondi residui al deadline (negativo se sforato). */
  remainingMs: number;
  /** ok / warning (<6h al deadline) / breached (deadline superato). */
  status: SlaStatus;
}

const HOUR_MS = 60 * 60 * 1000;
const WARNING_MS = 6 * HOUR_MS;

const WINDOWS: { label: SlaThreshold['label']; ms: number }[] = [
  { label: '24h', ms: 24 * HOUR_MS },
  { label: '72h', ms: 72 * HOUR_MS },
  { label: '14gg', ms: 14 * 24 * HOUR_MS },
];

function statusFor(remainingMs: number): SlaStatus {
  if (remainingMs <= 0) return 'breached';
  if (remainingMs < WARNING_MS) return 'warning';
  return 'ok';
}

/** Calcola le tre soglie SLA a partire dall'istante di detection. */
export function deriveKevSla(detectedAt: string, nowMs: number = Date.now()): SlaThreshold[] {
  const base = new Date(detectedAt).getTime();
  return WINDOWS.map(({ label, ms }) => {
    const remainingMs = base + ms - nowMs;
    return { label, remainingMs, status: statusFor(remainingMs) };
  });
}

/**
 * Millisecondi residui della soglia ancora aperta più vicina alla scadenza (per l'ordinamento
 * "SLA residuo crescente"). Se tutte sforate, ritorna il valore più negativo (più in ritardo prima).
 */
export function urgencyRemainingMs(thresholds: SlaThreshold[]): number {
  const open = thresholds.filter((t) => t.remainingMs > 0);
  if (open.length > 0) return Math.min(...open.map((t) => t.remainingMs));
  return Math.min(...thresholds.map((t) => t.remainingMs));
}

/**
 * Formatta un istante ISO in tempo relativo italiano (es. "30 minuti fa", "2 ore fa", "ieri").
 * Ritorna "mai" se l'istante è assente o non valido. Istanti futuri o entro il minuto → "poco fa".
 */
export function formatRelative(iso: string | null | undefined, nowMs: number = Date.now()): string {
  if (!iso) return 'mai';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 'mai';

  const diffMs = nowMs - t;
  if (diffMs < 60 * 1000) return 'poco fa';

  const min = Math.floor(diffMs / (60 * 1000));
  if (min < 60) return `${min} ${min === 1 ? 'minuto' : 'minuti'} fa`;

  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'ora' : 'ore'} fa`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'ieri';
  return `${days} giorni fa`;
}

/** Formatta i ms residui in stringa breve (es. "23h 10m", "scaduto"). */
export function formatRemaining(remainingMs: number): string {
  if (remainingMs <= 0) return 'scaduto';
  const totalMin = Math.floor(remainingMs / (60 * 1000));
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const min = totalMin % 60;
  if (days > 0) return `${days}g ${hours}h`;
  if (hours > 0) return `${hours}h ${min}m`;
  return `${min}m`;
}
